import React, { useState } from 'react';
import { ReportData } from '../types/index';
import { Button } from '../components/atoms/Button';
import { Card } from '../components/atoms/Card';


export const ReportsPage: React.FC = () => {
  const [reportData] = useState<ReportData>({
    totalTrades: 45,
    winRate: 67.5,
    totalProfit: 2340.50,
    avgProfit: 52.01,
    trades: [
      { id: '1', date: '2025-08-20', symbol: 'AAPL', side: 'BUY', quantity: 10, price: 170.25, profit: 125.50 },
      { id: '2', date: '2025-08-19', symbol: 'GOOGL', side: 'SELL', quantity: 5, price: 132.50, profit: -45.25 },
      { id: '3', date: '2025-08-18', symbol: 'MSFT', side: 'BUY', quantity: 15, price: 375.80, profit: 234.75 },
    ],
  });

  const exportData = (format: 'csv' | 'json') => {
    let blob: Blob;
    const fileName = `trading-report.${format}`;

    if (format === 'csv') {
      const csv = [
        'Date,Symbol,Side,Quantity,Price,Profit',
        ...reportData.trades.map(trade =>
          `${trade.date},${trade.symbol},${trade.side},${trade.quantity},${trade.price},${trade.profit}`
        ),
      ].join('\n');
      blob = new Blob([csv], { type: 'text/csv' });
    } else {
      const json = JSON.stringify(reportData.trades, null, 2);
      blob = new Blob([json], { type: 'application/json' });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="reports-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trading Reports</h1>
        <div className="flex gap-2">
          <Button onClick={() => exportData('csv')} data-testid="export-csv-button">
            Export CSV
          </Button>
          <Button variant="secondary" onClick={() => exportData('json')} data-testid="export-json-button">
            Export JSON
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="p-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{reportData.totalTrades}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Total Trades</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{reportData.winRate}%</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Win Rate</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">${reportData.totalProfit.toLocaleString()}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Total Profit</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">${reportData.avgProfit.toFixed(2)}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">Avg Profit</p>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Trade History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['Date', 'Symbol', 'Side', 'Quantity', 'Price', 'P&L'].map(header => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData.trades.map(trade => (
                <tr key={trade.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{trade.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {trade.symbol}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${trade.side === 'BUY'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}
                    >
                      {trade.side}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{trade.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">${trade.price}</td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${trade.profit >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                  >
                    ${trade.profit.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};